export type FriendlyError = {
  title: string;
  description: string;
  retryable: boolean;
};

const rawOf = (e: unknown): string => {
  if (e instanceof Error) return e.message;
  if (typeof e === 'string') return e;
  return '';
};

/**
 * Turn raw edge-function / network / gateway errors into calm, human copy.
 * Never surfaces status codes or stack noise to the user.
 */
export function friendlyError(e: unknown, fallbackTitle = 'Something went wrong'): FriendlyError {
  const raw = rawOf(e);
  const m = raw.toLowerCase();

  if (!m || m.includes('unknown')) {
    return {
      title: fallbackTitle,
      description: 'Please try again in a moment.',
      retryable: true,
    };
  }

  if (m.includes('timeout') || m.includes('timed out') || m.includes('aborted') || m.includes('504')) {
    return {
      title: 'That took too long',
      description:
        'The analysis ran out of time before it finished. Try a narrower question, or run it again.',
      retryable: true,
    };
  }

  if (m.includes('failed to fetch') || m.includes('networkerror') || m.includes('network request')) {
    return {
      title: 'Connection lost',
      description: 'We could not reach the server. Check your connection and try again.',
      retryable: true,
    };
  }

  if (m.includes('429') || m.includes('rate limit') || m.includes('too many requests')) {
    return {
      title: 'Busy right now',
      description: 'Too many requests at once. Wait about a minute, then try again.',
      retryable: true,
    };
  }

  if (m.includes('402') || m.includes('credit') || m.includes('quota') || m.includes('payment')) {
    return {
      title: 'Usage limit reached',
      description: 'The workspace has run out of AI credits. Top up to continue.',
      retryable: false,
    };
  }

  if (m.includes('403') || m.includes('forbidden') || m.includes('not authorized') || m.includes('permission')) {
    return {
      title: 'Not available to you',
      description: 'Your account does not have access to this. Ask an administrator if you need it.',
      retryable: false,
    };
  }

  if (m.includes('401') || m.includes('unauthorized') || m.includes('jwt') || m.includes('session')) {
    return {
      title: 'Session expired',
      description: 'Please sign in again to continue.',
      retryable: false,
    };
  }

  if (m.includes('404') || m.includes('not found')) {
    return {
      title: 'Nothing to show',
      description: 'We could not find what you asked for. It may have been moved or removed.',
      retryable: false,
    };
  }

  if (m.includes('non-2xx') || m.includes('internal') || m.includes('500')) {
    return {
      title: fallbackTitle,
      description: 'The server had a problem completing this. Please try again.',
      retryable: true,
    };
  }

  // Server already returned readable copy — pass it through.
  return { title: fallbackTitle, description: raw, retryable: true };
}
